// ──────────────────────────────────────────────────────────────
// QCARE-CHIP-VISIBILITY-POLICY (2026-05-12) — frontend mirror.
//
// Mirrors the backend `applyChipPolicy` from
// `apps/backend/src/modules/ai-chat/smart-chips.ts`. The frontend
// runs this defensively at render time on EVERY chip set —
// streaming responses (already filtered by the backend gate) AND
// historical `uiPayload.suggestions[]` rows persisted before the
// backend rollout. Same shape, same caps, so a previously-shown
// safety-route turn now shows 0 chips on reload without a DB
// rewrite.
//
// The frontend never makes the rules looser than the backend; both
// sides share the closed-enum below. Stack traces / raw JSON / chip
// metadata never reach the user — only the final clamped string[].
// ──────────────────────────────────────────────────────────────

const MAX_VISIBLE_CHIPS = 3;

/** Context the policy uses to pick a per-route cap. All fields
 *  optional so call sites can pass whatever they got from
 *  `message.meta` without runtime null-checks. */
export interface ChipPolicyContext {
    composedRoute?: string | null;
    layout?: string | null;
    taskType?: string | null;
    mode?: string | null;
    productsCount?: number;
}

/** Per-route chip cap. Values mirror the backend's `resolveChipCap`
 *  byte-for-byte; any divergence is a bug. */
function resolveChipCap(ctx: ChipPolicyContext): number {
    const route = (ctx.composedRoute ?? '').toLowerCase();
    const layout = (ctx.layout ?? '').toLowerCase();
    const taskType = (ctx.taskType ?? '').toLowerCase();
    const mode = (ctx.mode ?? '').toLowerCase();

    // QCARE-MINIMAL-CHIPS-V2.1 (2026-05-12) — see smart-chips.ts:resolveChipCap
    // for the canonical comment. Byte-for-byte frontend mirror; only
    // clarification (max 3) and image_candidate_list (max 2) keep
    // chips. Everything else (incl no-match / OOS, onboarding,
    // identity, recommendation, routine, store_faq, safety, default)
    // → 0. Historical persisted `uiPayload.suggestions[]` from pre-v2.1
    // turns are defensively clamped at render time.

    // (A) Safety routes — 0 chips.
    if (route.includes('safety')
        || route.includes('emergency')
        || route === 'medical_condition_safety'
        || route === 'prescription_escalation'
        || route === 'mole_safety'
        || route === 'mole_skin_lesion_safety') {
        return 0;
    }

    // (G) General chat / social greeting / tone-only updates — 0.
    if (
        route === 'social_greeting'
        || route === 'tone_preference_update'
        || taskType === 'general_chat'
        || mode === 'general_chat'
    ) {
        return 0;
    }

    // (E) Store FAQ — v2 tightens 1 → 0.
    if (layout === 'store_faq' || route === 'store_faq') {
        return 0;
    }

    // (D) No-match / OOS / image_no_match — v2.1 tightens 2 → 0.
    if (
        route === 'global_oos_no_match'
        || route === 'no_match'
        || route.endsWith('_no_catalog_match')
        || route.endsWith('_no_exact_match')
        || route === 'image_no_match'
        || route === 'beard_no_catalog_match'
    ) {
        return 0;
    }

    // (C) Routine — v2 tightens 3 → 0.
    if (
        mode === 'routine'
        || route === 'routine_usage_guidance'
        || route.startsWith('multiturn_routine_')
        || route === 'routine'
    ) {
        return 0;
    }

    // (F) Image candidate list — RECOVERY, keep 2.
    if (route === 'image_candidate_list' || layout === 'image_candidate_list') {
        return 2;
    }

    // (B) Direct product recommendation surfaces — v2 tightens 2 → 0.
    if (
        layout === 'hero_only'
        || layout === 'hero_plus_budget'
        || layout === 'top_n'
        || layout === 'multi'
        || layout === 'legacy_multi'
        || layout === 'face_advisory'
        || layout === 'hair_advisory'
        || layout === 'body_advisory'
        || layout === 'oral_advisory'
        || (ctx.productsCount ?? 0) > 0
    ) {
        return 0;
    }

    // (H) Clarification — v2 loosens 0 → 3. Checked LAST so it
    // can't promote a recommendation route over the (B) clamp.
    if (
        taskType === 'clarification_needed'
        || mode === 'clarify_first'
        || route === 'clarification_needed'
    ) {
        return 3;
    }

    // v2 default: 0 chips. Any text_only / unclassified route
    // (Training Hub identity, capability, product_count fallback,
    // anything we didn't enumerate above) drops to 0.
    return 0;
}

/** Apply the route-aware chip visibility policy. Returns the chips
 *  the user will actually see. Order is preserved (we slice the
 *  head of the list). Empty / null input returns []. Pure / never
 *  throws — safe to call from React render. */
export function applyChipPolicy(
    chips: ReadonlyArray<string> | null | undefined,
    ctx: ChipPolicyContext,
): string[] {
    const cap = resolveChipCap(ctx);
    if (cap <= 0) return [];
    if (!chips || chips.length === 0) return [];
    const effective = Math.min(cap, MAX_VISIBLE_CHIPS);
    return chips.slice(0, effective);
}
